import { z } from 'zod';
import { localeSchema } from './content.js';
/** Slot lengths Diana can choose per window. */
export const SLOT_LENGTHS = [30, 60, 90];
export const SLOT_LENGTH_LABELS = {
    30: '30 minut',
    60: '1 godzina',
    90: '1,5 godziny',
};
/**
 * A real calendar date, YYYY-MM-DD. Not just the shape: 2026-02-30 passed a
 * regex, reached Postgres and came back as a 500 (before that, a crash).
 */
export const isoDate = z.iso.date({ message: 'Data w formacie RRRR-MM-DD' });
/** Postgres returns 'HH:MM:SS'; the UI sends 'HH:MM'. Accept both. */
export const wallTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'Godzina w formacie GG:MM');
/**
 * The end of a span may also be 24:00, midnight at the end of the day — the
 * week grid and the hour editor produce it for the last hour, and refusing
 * it meant 23:00–24:00 could never be opened.
 */
export const wallEndTime = z
    .string()
    .regex(/^(([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?|24:00(:00)?)$/, 'Godzina w formacie GG:MM');
/* ── Availability (Diana's side) ───────────────────────────────────────
 * A window is wall-clock time on one date, in Diana's timezone. She can add
 * several per day (mornings and evenings).
 */
export const availabilityWindowInputSchema = z
    .object({
    date: isoDate,
    startTime: wallTime,
    endTime: wallEndTime,
    slotMinutes: z.union([z.literal(30), z.literal(60), z.literal(90)]),
})
    .refine((w) => w.endTime > w.startTime, {
    message: 'Koniec musi być późniejszy niż początek',
    path: ['endTime'],
})
    .refine((w) => {
    const mins = (t) => {
        const [h = '0', m = '0'] = t.split(':');
        return Number(h) * 60 + Number(m);
    };
    return mins(w.endTime) - mins(w.startTime) >= w.slotMinutes;
}, {
    // Otherwise she opens a window that can never produce a bookable slot.
    message: 'Okno jest krótsze niż jedna lekcja',
    path: ['slotMinutes'],
});
export const availabilityWindowSchema = z.object({
    id: z.uuid(),
    date: isoDate,
    startTime: wallTime,
    endTime: wallEndTime,
    slotMinutes: z.number().int(),
});
/* ── Booking (the student's side) ──────────────────────────────────────
 *
 * `pending` is a request, not a lesson: a student picked a package on the
 * public page and Diana still has to agree the weekly slot with them. It
 * holds the chosen slot so nobody else takes it in the meantime, and Diana
 * either confirms or cancels it from the calendar.
 */
export const BOOKING_STATUSES = ['pending', 'confirmed', 'cancelled', 'completed', 'no_show'];
export const BOOKING_STATUS_LABELS = {
    pending: 'Do potwierdzenia',
    confirmed: 'Potwierdzona',
    cancelled: 'Odwołana',
    completed: 'Odbyta',
    no_show: 'Nieobecność',
};
export const bookingInputSchema = z.object({
    /** ISO instant of the chosen slot. The server re-validates availability. */
    startsAt: z.iso.datetime(),
    offerId: z.uuid().nullable().optional(),
    name: z.string().min(1, 'Podaj imię').max(120),
    email: z.email('Nieprawidłowy adres e-mail').max(200),
    phone: z.string().max(40).optional(),
    level: z.string().max(20).optional(),
    goals: z.string().max(2000).optional(),
    /** IANA zone from the browser, so confirmations read correctly for them. */
    timezone: z.string().max(64).optional(),
    locale: localeSchema.optional(),
    /** Honeypot — permissive on purpose, see leadInputSchema. */
    website: z.string().max(200).optional(),
});
export const bookingSchema = z.object({
    id: z.uuid(),
    offerId: z.uuid().nullable(),
    /** Set when Diana books a lesson for one of her own students. */
    clientId: z.uuid().nullable(),
    name: z.string(),
    email: z.string(),
    phone: z.string(),
    level: z.string(),
    goals: z.string(),
    timezone: z.string(),
    locale: z.string(),
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    status: z.enum(BOOKING_STATUSES),
    /** What this lesson earns, in grosze. 0 for the free intro call. */
    priceGrosz: z.number().int(),
    meetingUrl: z.string(),
    notes: z.string(),
    createdAt: z.coerce.date(),
});
export const bookingPatchSchema = z.object({
    status: z.enum(BOOKING_STATUSES).optional(),
    clientId: z.uuid().nullable().optional(),
    priceGrosz: z.number().int().nonnegative().optional(),
    meetingUrl: z.string().max(500).optional(),
    notes: z.string().max(4000).optional(),
});
/**
 * A lesson Diana puts in the calendar herself, for a student she already
 * has. Wall-clock in her timezone — the server turns it into an instant, so
 * the browser never does timezone arithmetic.
 */
export const studioBookingInputSchema = z.object({
    clientId: z.uuid(),
    date: isoDate,
    startTime: wallTime,
    minutes: z.union([z.literal(30), z.literal(60), z.literal(90)]),
    /** Omit to use the client's rate (or their open package's per-lesson price). */
    priceGrosz: z.number().int().nonnegative().optional(),
    notes: z.string().max(4000).optional(),
});
/* ── Bulk calendar edits (Diana's week grid) ───────────────────────────
 * She selects hours in the week view and says what they are. One request
 * for the whole selection: seven days of clicking should not be seven
 * round trips, and half-applied is not a state the calendar should have.
 */
export const availabilitySpanSchema = z.object({
    date: isoDate,
    startTime: wallTime,
    endTime: wallEndTime,
});
/**
 * What a selection of hours becomes:
 *   open   offered on the website
 *   off    time she does not work — drawn in her colour, never bookable
 *   clear  neither: an ordinary empty hour
 *
 * 'close' is the old name for 'off' and is still accepted, so a browser tab
 * left open across a deploy does not fail on its next click.
 */
export const BULK_ACTIONS = ['open', 'off', 'clear'];
export const availabilityBulkSchema = z.object({
    action: z
        .enum(['open', 'off', 'clear', 'close'])
        .transform((a) => (a === 'close' ? 'off' : a)),
    slotMinutes: z.union([z.literal(30), z.literal(60), z.literal(90)]).default(60),
    /** Optional note on time off: "urlop", "lekarz". Shown in the calendar. */
    label: z.string().trim().max(80).default(''),
    // Capped: the week grid can only produce a few dozen, and an unbounded
    // list would be an easy way to make the server do unbounded work.
    spans: z.array(availabilitySpanSchema).min(1).max(200),
});
/* ── Time off ──────────────────────────────────────────────────────────
 * Hours Diana has explicitly marked as not working. Deliberately a record
 * rather than the absence of one: an empty hour and an hour she has blocked
 * look identical in a database that only stores availability, and she wants
 * to see the difference.
 */
export const timeOffSchema = z.object({
    id: z.uuid(),
    date: isoDate,
    startTime: wallTime,
    endTime: wallEndTime,
    label: z.string(),
});
/**
 * Copy a week's timetable onto another week.
 *
 * `clientIds` is the point of the whole feature: Diana ticks the students
 * who are still coming, and only their lessons come across. An empty list
 * copies no lessons at all, which is how "just the open hours" works.
 */
export const copyWeekSchema = z.object({
    /** Any day in the source week; the server snaps both to Monday. */
    from: isoDate,
    to: isoDate,
    /** Also copy the hours that are open for website booking. */
    windows: z.boolean().default(true),
    clientIds: z.array(z.uuid()).max(500).default([]),
});
/** One bookable slot as sent to the browser. */
export const slotSchema = z.object({
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
    minutes: z.number().int(),
});
